import RenderFields from "../../../common/ui/render-fields";
import React, {useEffect, useState} from "react";
import _ from "lodash";
import {DESIGN_META_DETAIL_FIELD_LIST} from "../../constants/design.constant";
import {updateSingleDesign} from "../../services/design-data-service";
import {syncShopifyProducts} from "../../../common/services/cloud-function-service";
import {generateContent, generateJsonContent} from "../../../common/services/open-ai-service";
import {COLLECTION_INPUT_FIELDS} from "../../../shopify/constants/shopify-blog.constant";
import {
    prepareProductDescriptionMetaPromptMessages,
    prepareProductDescriptionPromptMessages
} from "../../utils/open-ai-prompt-util";
import ShowProgressSpinner from "../../../common/ui/show-progress-spinner";

const REMOVE_TEXT_LIST = {
    MIN_AGO: 'min ago',
    JUST_NOW: 'Just now',
    PRODUCT_DESCRIPTION: 'Product Description'
};

const defaultMetaDetail = {
    descriptionInput: '',
    descriptionList: [],
}

const defaultFilterCriteria = {
    toneOfVoice: 'Catchy',
}

const DesignMetaDetail = ({designDetail, setDesignDetail}) => {
    const [metaDetail, setMetaDetail] = useState(defaultMetaDetail);
    const [initialStep, setInitialStep] = useState(true);
    const [dataFetchInProgress, setDataFetchInProgress] = useState(false);
    const [filterCriteria, setFilterCriteria] = useState(defaultFilterCriteria);

    useEffect(() => {
        if (designDetail) {
            setMetaDetail(designDetail.meta || defaultMetaDetail);
        }
    }, [initialStep]);

    const handleDetailChanges = async (event) => {
        const {name, value, type, cleanup} = event.target;
        const existingMetaDetail = {...metaDetail};

        if (type === 'checkbox') {
            existingMetaDetail[name] = !existingMetaDetail[name];
        } else if (type === 'number') {
            existingMetaDetail[name] = _.parseInt(value);
        } else {
            existingMetaDetail[name] = value;
        }

        setMetaDetail(existingMetaDetail);
        await prepareMetaDescriptionList(existingMetaDetail, cleanup);
    }

    const prepareMetaDescriptionList = async (existingMetaDetail, cleanup = false) => {
        const tempDescriptionList = _.split(existingMetaDetail.descriptionInput, '\n');
        const descriptionList = [];

        _.forEach(tempDescriptionList, description => {
            const trimmedDescription = _.trim(description);
            if (!_.isEmpty(trimmedDescription)) {
                descriptionList.push(trimmedDescription);
            }
        });

        existingMetaDetail.descriptionList = descriptionList;

        if (cleanup) {
            await cleanupDescriptionInput(existingMetaDetail);
        } else {
            const existingDesignDetail = {...designDetail};
            existingDesignDetail.meta = existingMetaDetail;
            setDesignDetail(existingDesignDetail);
            await updateDesign(existingDesignDetail);
        }
    }

    const updateDesign = async (existingDesignDetail) => {
        await updateSingleDesign(existingDesignDetail);
    }

    const cleanupDescriptionInput = async (currentMetaDetail?) => {
        const existingMetaDetail = currentMetaDetail || {...metaDetail};
        const updatedDescriptionList = [];

        _.forEach(existingMetaDetail.descriptionList, description => {
            if (description.indexOf(REMOVE_TEXT_LIST.MIN_AGO) === -1 && description.indexOf(REMOVE_TEXT_LIST.JUST_NOW) === -1 &&
                description.indexOf(REMOVE_TEXT_LIST.PRODUCT_DESCRIPTION) === -1) {
                updatedDescriptionList.push(description);
            }
        });

        existingMetaDetail.descriptionList = updatedDescriptionList;
        existingMetaDetail.descriptionInput = _.join(existingMetaDetail.descriptionList, '\n\n');
        setMetaDetail(existingMetaDetail);

        const existingDesignDetail = {...designDetail};
        existingDesignDetail.meta = existingMetaDetail;
        setDesignDetail(existingDesignDetail);
        await updateDesign(existingDesignDetail);
    }

    const generateProductDescriptions = async () => {
        setDataFetchInProgress(true);
        const promptMessages = prepareProductDescriptionMetaPromptMessages(designDetail, metaDetail);
        const contentDetail: any = await generateJsonContent(promptMessages);

        if (contentDetail) {
            const existingDesignDetail = {...designDetail};
            existingDesignDetail.content = contentDetail;
            setDesignDetail(existingDesignDetail);
            await updateDesign(existingDesignDetail);
        }

        setDataFetchInProgress(false);
    }

    return (
        <>

            <div className="flex justify-between items-center">
                <div className="text-lg mt-1 font-semibold flex justify-start">
                    Design Meta Description <span
                    className="text-purple-600 font-bold ml-2">({metaDetail?.descriptionList?.length || 0})</span>
                </div>

                <button
                    className="button-gray px-4 py-2 flex items-center justify-center"
                    onClick={() => {
                        cleanupDescriptionInput();
                    }}>
                    <span>Cleanup Description</span>
                </button>

                <div className="flex justify-end items-center w-1/2">
                    <ShowProgressSpinner
                        dataFetchInProgress={dataFetchInProgress}/>
                    <RenderFields
                        parentForm={metaDetail}
                        handleDetailChanges={handleDetailChanges}
                        fieldList={_.groupBy(COLLECTION_INPUT_FIELDS, 'group')['meta']}
                        enableEdit={true}
                        noFieldLabel={true}
                        fieldClassName={'w-full'}/>
                    <button
                        className="button-green px-4 py-2 flex items-center justify-center"
                        onClick={generateProductDescriptions}>
                        <span>Generate Descriptions</span>
                    </button>
                </div>
            </div>

            <div className="flex justify-start -ml-2 mt-2">

                <div
                    className="w-1/2 flex flex-wrap justify-start">
                    <RenderFields
                        parentForm={metaDetail}
                        handleDetailChanges={(event) => {
                            handleDetailChanges(event)
                        }}
                        fieldList={DESIGN_META_DETAIL_FIELD_LIST}
                        enableEdit={true}
                        noFieldLabel={true}
                        fieldClassName={'w-full'}/>
                </div>

                <div
                    className="w-1/2 meta-description-list-container overflow-y-scroll bg-gray-300 border border-gray-400 p-2 ml-4 mt-2">

                    {metaDetail?.descriptionList?.map((description, index) => (
                        <div key={`description-${index}`}
                             className="flex justify-start">
                            <div
                                className="flex justify-center self-center text-2xl text-gray-500 w-10">{index + 1}</div>
                            <div className="w-full m-2 p-4 border border-gray-400 bg-white rounded-xl">
                                {description}
                            </div>
                            <div
                                className="flex justify-center self-center text-xl text-purple-600 w-10 mx-2">{description?.length}</div>
                        </div>
                    ))}

                </div>

            </div>


        </>
    )
}

export default DesignMetaDetail
